import SongWithSort from "./SongWithSort";

/**
 * 勝ち抜き戦で1位を決める。
 * 1試合は最大 groupSize 曲の中から1曲を選ぶ形式。
 *
 * 生き残りの曲は待ち行列で管理し、先頭から groupSize 曲を1組にする。
 * 勝者は行列の末尾に戻り、敗者は脱落する。
 * また、他の生き残りに負けることが（推移律も含めて）分かっている曲は
 * 試合をせずに脱落させる。これにより、組の中の曲は常に互いに未決着になり、
 * 生き残りが groupSize 曲未満になる決勝以外は必ず groupSize 曲の組になる。
 */
export default class Tournament {
  private alive: SongWithSort[];

  private readonly groupSize: number;

  constructor(songs: SongWithSort[], groupSize: number = 2) {
    this.alive = songs.slice();
    this.groupSize = groupSize;
    this.eliminateKnownLosers();
  }

  public winner(): SongWithSort {
    if (this.alive.length !== 1) {
      throw new Error("Winner is not decided yet.");
    }
    return this.alive[0];
  }

  public hasNext(): boolean {
    return this.alive.length > 1;
  }

  /** 次の試合の対戦曲。互いに未決着の曲だけが含まれる。 */
  public next(): SongWithSort[] {
    return this.alive.slice(0, this.groupSize);
  }

  /** 次の試合が決勝（生き残り全員が対戦する）かどうか。 */
  public isFinal(): boolean {
    return this.alive.length <= this.groupSize;
  }

  /**
   * 現在の組の勝者を指定する。組の他の曲は敗退し、勝者は次のラウンドへ進む。
   */
  public select(winner: SongWithSort) {
    const group = this.next();
    if (!group.find(v => v.id === winner.id)) {
      throw new Error(`Specified song is not in current group: ${winner.id}, ${winner.name}`);
    }
    group.forEach(loser => {
      if (loser.id !== winner.id) {
        winner.win(loser);
        loser.loose(winner);
      }
    });
    this.alive = this.alive
      .filter(v => !group.find(g => g.id === v.id))
      .concat([winner]);
    this.eliminateKnownLosers();
  }

  private eliminateKnownLosers() {
    const alive = this.alive;
    this.alive = alive.filter(song => !alive.some(other => other !== song && other.isWin(song)));
  }
}
